import React, { type ComponentProps, ReactNode, useMemo } from 'react';

import { CSSProp } from 'styled-components';

import { useAutocomplete } from '../hooks';
import type {
  GenericItem,
  UseAutocompleteProps,
  VirtualProps,
  RenderGroupHeaderProps,
} from '../types';
import { ItemToString, RenderMenuItem } from '../types';
import AutocompleteControl from './Control/AutocompleteControl';
import DropdownMenu from './Menu/DropdownMenu';
import { filterUndefined } from '../helpers/filterUndefined';
import { useFilteredMenuProps } from '../helpers/useFilteredMenuProps';

type ControlProps<TItem extends GenericItem> = ComponentProps<
  typeof AutocompleteControl<TItem>
>;

type MenuProps<TItem extends GenericItem> = ComponentProps<
  typeof DropdownMenu<TItem>
>;

export type AutocompleteTemplateProps<TItem extends GenericItem> =
  UseAutocompleteProps<TItem> & {
    style?: CSSProp;
    /**
     * Disable the entire dropdown
     */
    disabled?: boolean;
    /**
     * Enable error state
     */
    error?: boolean;
    /**
     * Enable loading state
     */
    isLoading?: boolean;
    /**
     * Placeholder text to display
     */
    placeholder?: string;
    /**
     * Placeholder text to display in the search input when the dropdown is open or focused (only applies to autocomplete variants)
     */
    searchPlaceholder?: string;
    /**
     * Function to render a custom dropdown control
     * @param args Props to be provided to the control
     * @returns A dropdown control element
     */
    renderControl?: (args: ControlProps<TItem>) => React.ReactNode;
    /**
     * Function to render a custom dropdown menu
     * @param args Props to be provided to the menu
     * @param virtualProps Props used to virtualize the menu - see stories for examples
     * @returns A dropdown menu element
     */
    renderMenu?: (
      args: MenuProps<TItem>,
      virtualProps: VirtualProps,
    ) => React.ReactNode;
    /**
     * Function to render a custom selected item
     * @param selectedItem The selected item
     * @param itemToString Function to convert an item to a string
     * @param isOpen Whether the dropdown is open
     */
    renderItem?: ({
      selectedItem,
      itemToString,
      isOpen,
    }: {
      selectedItem?: TItem | null;
      itemToString: ItemToString<TItem>;
      isOpen: boolean;
    }) => ReactNode;
    /**
     * Whether to use less spacing for a more compact UI
     */
    highDensity?: boolean;
    /**
     * Whether to highlight the toggle button on focus
     */
    highlightButtonOnFocus?: boolean;
    /**
     * Function to render a custom menu item
     */
    renderMenuItem?: RenderMenuItem<TItem>;
    /**
     * Function to render a custom group header menu item
     */
    renderGroupHeader?: (props: RenderGroupHeaderProps) => ReactNode;
    /**
     * Function to render a component that will appear when there are no menu items
     */
    renderNoAutocompleteResults?: () => ReactNode;
    /**
     * Enable highlighting of search text in menu items
     */
    enableSearchHighlight?: boolean;
    /**
     * The id of the element that labels the dropdown menu.
     */
    'aria-labelledby'?: string;
    /**
     * Custom max-height for the menu container in pixels. Defaults to 300.
     */
    menuHeight?: number;
  };

const defaultRenderControl = <TItem extends GenericItem>(
  args: ControlProps<TItem>,
) => <AutocompleteControl {...args} />;

/**
 * A single select, searchable dropdown component.
 *
 * This component covers the majority of use cases, but advanced use cases may require using the `useAutocomplete` hook directly.
 */
const Autocomplete = <TItem extends GenericItem>({
  style,
  disabled,
  error,
  isLoading,
  placeholder,
  searchPlaceholder,
  renderControl = defaultRenderControl,
  renderMenu = DropdownMenu.render,
  renderItem,
  renderGroupHeader,
  highDensity,
  highlightButtonOnFocus,
  renderMenuItem,
  renderNoAutocompleteResults,
  enableSearchHighlight,
  menuHeight,
  'aria-labelledby': ariaLabelledBy,
  preserveItemOrder,
  ...args
}: AutocompleteTemplateProps<TItem>) => {
  const { controlProps, menuProps, virtualProps } = useAutocomplete({
    ...args,
    preserveItemOrder,
  });

  // Filter out undefined props to ensure they are not passed to the control
  // as explicitly undefined and overwrite other props
  const filteredControlProps = useMemo(
    () =>
      filterUndefined({
        disabled,
        error,
        isLoading,
        placeholder,
        searchPlaceholder,
        renderItem,
        highDensity,
        highlightButtonOnFocus,
      }),
    [
      disabled,
      error,
      isLoading,
      placeholder,
      searchPlaceholder,
      renderItem,
      highDensity,
      highlightButtonOnFocus,
    ],
  );
  const filteredMenuProps = useFilteredMenuProps({
    renderMenuItem,
    renderGroupHeader,
    renderNoAutocompleteResults,
    enableSearchHighlight,
    menuHeight,
    'aria-labelledby': ariaLabelledBy,
  });

  return (
    <div css={[{ width: 300 }, style]}>
      {renderControl({
        ...filteredControlProps,
        ...controlProps,
      })}
      {renderMenu({ ...menuProps, ...filteredMenuProps }, virtualProps)}
    </div>
  );
};

export default Autocomplete;
